import { useQuery } from '@tanstack/react-query'
import type { z } from 'zod'

import { api } from '@/api/client'
import { EntityCrud } from '@/components/entity-crud'

import { fetchManufacturers, manufacturersKey, type Manufacturer } from './api'
import { CONTACT_FIELDS, contactSchema, emptyContact, toContactValues } from './contact'

const schema = contactSchema
type Values = z.infer<typeof schema>

export function ManufacturersScreen() {
  const query = useQuery({ queryKey: manufacturersKey, queryFn: fetchManufacturers })

  return (
    <EntityCrud<Manufacturer, Values>
      entity="manufacturers"
      singular="manufacturer"
      rows={query.data}
      isPending={query.isPending}
      error={query.error}
      queryKey={manufacturersKey}
      searchable
      attachmentOwnerType="manufacturer"
      keyOf={(row) => row.id}
      labelOf={(row) => row.name}
      columns={[
        { header: 'Name', cell: (row) => <span className="font-medium">{row.name}</span> },
        {
          header: 'Website',
          cell: (row) =>
            row.website ? (
              <a
                href={row.website}
                target="_blank"
                rel="noreferrer noopener"
                className="text-primary hover:underline"
              >
                {row.website.replace(/^https?:\/\//, '')}
              </a>
            ) : (
              <span className="text-muted-foreground">—</span>
            ),
        },
        {
          header: 'Comment',
          cell: (row) => <span className="text-muted-foreground">{row.comment || '—'}</span>,
        },
      ]}
      fields={CONTACT_FIELDS}
      schema={schema}
      emptyValues={emptyContact}
      toValues={toContactValues}
      onCreate={(values) => api.POST('/api/manufacturers', { body: values })}
      onUpdate={(row, values) =>
        api.PUT('/api/manufacturers/{id}', { params: { path: { id: row.id } }, body: values })
      }
      onDelete={(row) =>
        api.DELETE('/api/manufacturers/{id}', { params: { path: { id: row.id } } })
      }
    />
  )
}
